import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

// Common timezones in Australia
const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACDT/ACST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland, NZ (NZDT/NZST)' }
];

export default function AdminBusinessSettings() {
  const [businessName, setBusinessName] = useState('');
  const [timezone, setTimezone] = useState('Australia/Sydney');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/business`);
      setBusinessName(response.data.business_name);
      setTimezone(response.data.timezone || 'Australia/Sydney');
    } catch (error) {
      toast.error('Failed to fetch business settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!businessName.trim()) {
      toast.error('Business name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/business`, { 
        business_name: businessName,
        timezone: timezone
      });
      toast.success('Settings updated! Calendar times will now use your timezone.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="retro-panel text-center py-12">
        <div className="text-2xl glow-text" style={{ color: '#FF6B00' }}>
          [LOADING SETTINGS...]
        </div>
      </div>
    );
  }

  return (
    <div className="retro-panel">
      <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
        [BUSINESS SETTINGS]
      </h2>

      <form onSubmit={handleSave} className="space-y-4 md:space-y-6">
        <div>
          <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
            Business Name:
          </label>
          <input
            type="text"
            data-testid="business-name-input"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            className="retro-input w-full"
            placeholder="Enter your business name"
            required
          />
          <div className="mt-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            This name appears throughout the app
          </div>
        </div>

        <div>
          <label className="block mb-2 text-lg font-semibold" style={{ color: '#00FF00' }}>
            Timezone (for Calendar Events):
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="retro-input w-full"
            required
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <div className="mt-2 text-sm" style={{ color: 'rgba(0, 255, 0, 0.7)' }}>
            ✓ Calendar invites will use this timezone
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            data-testid="save-business-settings-button"
            className="retro-btn retro-btn-green w-full md:w-auto"
            disabled={saving}
          >
            {saving ? '[SAVING...]' : '[SAVE CHANGES]'}
          </button>
        </div>

        <div className="mt-6 p-4 border-2" style={{ borderColor: 'rgba(255, 107, 0, 0.3)', background: 'rgba(255, 107, 0, 0.05)' }}>
          <div className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            <strong style={{ color: '#FF6B00' }}>Current Preview:</strong>
            <div className="mt-3 text-2xl md:text-3xl font-bold glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
              {businessName || 'it IN A bit'}
            </div>
            <div className="mt-2 text-sm" style={{ color: '#00FF00' }}>
              Timezone: {TIMEZONES.find(tz => tz.value === timezone)?.label || timezone}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
